// Drawing on the map (issue 1032): the draw tool's pure parts. A stroke is simplified before it is sent
// (Ramer–Douglas–Peucker, 1 px); a point is kept within one square of the map (design/28 §1); and an Erase click takes
// the topmost drawing under the pointer that is the person's to erase — their own, or for the DM one whose author has
// left — never anyone else's, which is also all that lights up under the pointer.
import { DRAW_LIMITS, mayEraseDrawing, type Drawing, type Role } from '@dragons-table/vtt-shared';

export interface Pt { x: number; y: number }

const half = (v: number) => Math.round(v * 2) / 2;

/** The distance from p to the segment (x0, y0)–(x1, y1). */
export function distToSegment(p: Pt, x0: number, y0: number, x1: number, y1: number): number {
  const dx = x1 - x0; const dy = y1 - y0;
  const len2 = dx * dx + dy * dy;
  const k = len2 === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - x0) * dx + (p.y - y0) * dy) / len2));
  return Math.hypot(p.x - (x0 + k * dx), p.y - (y0 + k * dy));
}

/** Flat x, y pairs, simplified to within `tolerance` px and rounded to half a pixel; at least two points (a dot is two). */
export function simplify(flat: number[], tolerance = 1): number[] {
  const n = Math.floor(flat.length / 2);
  if (n === 0) return [];
  if (n === 1) return [half(flat[0]!), half(flat[1]!), half(flat[0]!), half(flat[1]!)];
  const keep = new Uint8Array(n);
  keep[0] = 1; keep[n - 1] = 1;
  const stack: Array<[number, number]> = [[0, n - 1]];
  while (stack.length) {
    const [a, b] = stack.pop()!;
    let far = -1; let worst = tolerance;
    for (let k = a + 1; k < b; k += 1) {
      const dist = distToSegment({ x: flat[2 * k]!, y: flat[2 * k + 1]! }, flat[2 * a]!, flat[2 * a + 1]!, flat[2 * b]!, flat[2 * b + 1]!);
      if (dist > worst) { worst = dist; far = k; }
    }
    if (far >= 0) { keep[far] = 1; stack.push([a, far], [far, b]); }
  }
  const out: number[] = [];
  for (let k = 0; k < n; k += 1) if (keep[k]) out.push(half(flat[2 * k]!), half(flat[2 * k + 1]!));
  // A stroke the server would refuse for length keeps its shape at a coarser tolerance.
  if (out.length / 2 > DRAW_LIMITS.strokePoints) return simplify(flat, tolerance * 2);
  return out;
}

/** A point kept on the map, or up to one square (`pitch` px) past its edge. */
export function clampToBoard(p: Pt, extent: { width: number; height: number }, pitch: number): Pt {
  const m = Math.max(0, pitch);
  return { x: Math.max(-m, Math.min(extent.width + m, p.x)), y: Math.max(-m, Math.min(extent.height + m, p.y)) };
}

/** A rectangle's or an ellipse's box, whichever corner it was dragged from. */
export function shapeBox(d: Drawing): { x0: number; y0: number; x1: number; y1: number } {
  const [a, b, c, e] = d.points as [number, number, number, number];
  return { x0: Math.min(a, c), y0: Math.min(b, e), x1: Math.max(a, c), y1: Math.max(b, e) };
}

/** A text drawing's box on the board: one line from its anchor (top left). An estimate; the canvas measures nothing here. */
export function textBox(d: Drawing): { x0: number; y0: number; x1: number; y1: number } {
  const size = d.size ?? 16;
  const x0 = d.points[0]!; const y0 = d.points[1]!;
  return { x0, y0, x1: x0 + Math.max(1, (d.text ?? '').length) * size * 0.6, y1: y0 + size * 1.25 };
}

/**
 * Whether p is on a drawing: its line, within its width and `slop`; a shape's outline; text's box. `slop` is in BOARD
 * pixels and the board passes six SCREEN pixels' worth (6 / zoom): a fixed six board pixels was half a screen pixel on
 * a phone's zoomed-out board, where nothing could be erased (2026-10-05).
 */
export function hitsDrawing(d: Drawing, p: Pt, slop = 6): boolean {
  const tol = d.width / 2 + slop;
  const pts = d.points;
  if (d.kind === 'text') { const b = textBox(d); const m = Math.max(4, slop - 2); return p.x >= b.x0 - m && p.x <= b.x1 + m && p.y >= b.y0 - m && p.y <= b.y1 + m; }
  if (d.kind === 'stroke' || d.kind === 'line') {
    for (let k = 0; k + 3 < pts.length; k += 2) if (distToSegment(p, pts[k]!, pts[k + 1]!, pts[k + 2]!, pts[k + 3]!) <= tol) return true;
    return false;
  }
  const b = shapeBox(d);
  if (d.kind === 'rect') {
    return distToSegment(p, b.x0, b.y0, b.x1, b.y0) <= tol || distToSegment(p, b.x1, b.y0, b.x1, b.y1) <= tol
      || distToSegment(p, b.x1, b.y1, b.x0, b.y1) <= tol || distToSegment(p, b.x0, b.y1, b.x0, b.y0) <= tol;
  }
  // An ellipse: the distance to its outline along the ray from its centre.
  const cx = (b.x0 + b.x1) / 2; const cy = (b.y0 + b.y1) / 2; const rx = Math.max(0.5, (b.x1 - b.x0) / 2); const ry = Math.max(0.5, (b.y1 - b.y0) / 2);
  const dist = Math.hypot(p.x - cx, p.y - cy);
  const t = Math.hypot((p.x - cx) / rx, (p.y - cy) / ry);
  const off = t === 0 ? Math.min(rx, ry) : Math.abs(dist / t - dist);
  return off <= tol;
}

export const mayErase = (d: Drawing, meId: string, role: Role): boolean => mayEraseDrawing(d, { id: meId, role });

/** The topmost drawing under p this person may erase, or null. Somebody else's is never a candidate. */
export function erasableAt(drawings: readonly Drawing[], p: Pt, meId: string, role: Role, slop = 6): Drawing | null {
  for (let k = drawings.length - 1; k >= 0; k -= 1) {
    const d = drawings[k]!;
    if (mayErase(d, meId, role) && hitsDrawing(d, p, slop)) return d;
  }
  return null;
}

/** This person's most recent drawing on the scene (Ctrl+Z erases it), or null. */
export function lastOwn(drawings: readonly Drawing[], meId: string): Drawing | null {
  for (let k = drawings.length - 1; k >= 0; k -= 1) if (drawings[k]!.ownerId === meId) return drawings[k]!;
  return null;
}
